import { useState } from "react";
import { createPortal } from "react-dom";
import { Download, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import {
  downloadReimbSyncFailExcel,
  formatCtcInr,
  normalizeReimbSyncFailRows,
} from "../../utils/structureReimbSyncReport";

/**
 * Rectangular failure report: Emp Code | Name | CTC | Error + Excel export.
 */
export default function StructureReimbSyncFailReportModal({
  open,
  title = "Structure sync — failed employees",
  meta = {},
  errors = [],
  onClose,
}) {
  const [exporting, setExporting] = useState(false);
  if (!open) return null;

  const rows = normalizeReimbSyncFailRows(errors, meta.failedSummary);
  const failedCount = meta.failed != null ? meta.failed : rows.length;

  const onExport = async () => {
    if (!rows.length) {
      toast.error("No failed rows to export");
      return;
    }
    setExporting(true);
    try {
      await downloadReimbSyncFailExcel({ errors, meta });
      toast.success("Excel downloaded");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[12020] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-none border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] sm:rounded-sm">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">{title}</h3>
            <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
              Updated {meta.updated ?? "—"} · Skipped {meta.skipped ?? "—"} · Failed{" "}
              {failedCount}
              {meta.effectiveFrom ? ` · Effective from ${meta.effectiveFrom}` : ""}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={exporting || !rows.length}
              onClick={onExport}
              className="inline-flex items-center gap-1.5 rounded-sm border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-xs font-medium text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] disabled:opacity-50"
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Export Excel
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-sm text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              No failure details available for this report.
            </p>
          ) : (
            <div className="overflow-hidden rounded-sm border border-[hsl(var(--ds-border))]">
              <table className="w-full min-w-[640px] border-collapse text-left text-xs">
                <thead>
                  <tr className="bg-[hsl(210_40%_96%)] text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-fg))]">
                    <th className="border border-[hsl(var(--ds-border))] px-3 py-2.5 w-14">
                      Sl
                    </th>
                    <th className="border border-[hsl(var(--ds-border))] px-3 py-2.5 w-32">
                      Employee code
                    </th>
                    <th className="border border-[hsl(var(--ds-border))] px-3 py-2.5">
                      Name
                    </th>
                    <th className="border border-[hsl(var(--ds-border))] px-3 py-2.5 w-36 text-right">
                      Current CTC
                    </th>
                    <th className="border border-[hsl(var(--ds-border))] px-3 py-2.5">
                      Error detail
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={`${r.sl}-${r.empCode}-${String(r.error).slice(0, 24)}`}
                      className="bg-[hsl(var(--ds-surface))]"
                    >
                      <td className="border border-[hsl(var(--ds-border))] px-3 py-2 tabular-nums text-[hsl(var(--ds-muted-fg))]">
                        {r.sl}
                      </td>
                      <td className="border border-[hsl(var(--ds-border))] px-3 py-2 font-medium text-[hsl(var(--ds-fg))]">
                        {r.empCode}
                      </td>
                      <td className="border border-[hsl(var(--ds-border))] px-3 py-2 text-[hsl(var(--ds-fg))]">
                        {r.name}
                      </td>
                      <td className="border border-[hsl(var(--ds-border))] px-3 py-2 text-right tabular-nums text-[hsl(var(--ds-fg))]">
                        {formatCtcInr(r.ctc)}
                      </td>
                      <td className="border border-[hsl(var(--ds-border))] px-3 py-2 leading-relaxed text-[hsl(var(--ds-muted-fg))]">
                        {r.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm border border-[hsl(var(--ds-border))] px-4 py-2 text-xs font-medium text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
